import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

// eslint-disable-next-line no-restricted-imports -- pre-existing thunk-level apiClient usage; migrate to api/<domain>.js when touched
import apiClient from '@sophocles/http-common';
import { checkHttpStatus } from '@sophocles/utils';

// Entity adapters for normalized state management
const projectJobsAdapter = createEntityAdapter({
  selectId: job => job.id,
  sortComparer: (a, b) => new Date(b.installation_date) - new Date(a.installation_date),
});

const servicesPerformedAdapter = createEntityAdapter({
  selectId: service => service.id,
  sortComparer: (a, b) => new Date(b.service_date) - new Date(a.service_date),
});

const siteGroupsAdapter = createEntityAdapter({
  selectId: siteGroup => siteGroup.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const sitesAdapter = createEntityAdapter({
  selectId: site => site.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

// Enhanced async thunks for project jobs
export const fetchProjectJob = createAsyncThunk('projectServices/fetchProjectJob', async jobId => {
  const response = await apiClient.get(`/jobs/${jobId}/`);
  checkHttpStatus(response);
  return response.data;
});

export const updateProjectJob = createAsyncThunk('projectServices/updateProjectJob', async ({ jobId, jobData }) => {
  const response = await apiClient.patch(`/jobs/${jobId}/`, jobData);
  checkHttpStatus(response);
  return response.data;
});

export const deleteProjectJob = createAsyncThunk('projectServices/deleteProjectJob', async jobId => {
  const response = await apiClient.delete(`/jobs/${jobId}/`);
  checkHttpStatus(response);
  return jobId;
});

export const approveProjectJob = createAsyncThunk('projectServices/approveProjectJob', async jobId => {
  const response = await apiClient.patch(`/jobs/${jobId}/`, { pending: false });
  checkHttpStatus(response);
  return response.data;
});

// Enhanced async thunks for services performed
export const fetchServicePerformed = createAsyncThunk('projectServices/fetchServicePerformed', async serviceId => {
  const response = await apiClient.get(`/performedservices/${serviceId}/`);
  checkHttpStatus(response);
  return response.data;
});

export const updateServicePerformed = createAsyncThunk(
  'projectServices/updateServicePerformed',
  async ({ serviceId, serviceData }) => {
    const response = await apiClient.patch(`/performedservices/${serviceId}/`, serviceData);
    checkHttpStatus(response);
    return response.data;
  },
);

export const deleteServicePerformed = createAsyncThunk('projectServices/deleteServicePerformed', async serviceId => {
  const response = await apiClient.delete(`/performedservices/${serviceId}/`);
  checkHttpStatus(response);
  return serviceId;
});

// Async thunks for service aggregations
export const fetchServiceAggregationsByDate = createAsyncThunk(
  'projectServices/fetchServiceAggregationsByDate',
  async (params = {}) => {
    const response = await apiClient.get('/totals/bydateandsitegroup/', { params });
    checkHttpStatus(response);
    return response.data;
  },
);

export const fetchServiceAggregationsByService = createAsyncThunk(
  'projectServices/fetchServiceAggregationsByService',
  async (params = {}) => {
    const response = await apiClient.get('/totals/byservice/', { params });
    checkHttpStatus(response);
    return response.data;
  },
);

export const fetchServiceAggregationsByCrewService = createAsyncThunk(
  'projectServices/fetchServiceAggregationsByCrewService',
  async (params = {}) => {
    const response = await apiClient.get('/totals/bycrewandservice/', { params });
    checkHttpStatus(response);
    return response.data;
  },
);

// Async thunks for project jobs
export const fetchProjectJobs = createAsyncThunk('projectServices/fetchProjectJobs', async (params = {}) => {
  const response = await apiClient.get('/jobs/', { params });
  checkHttpStatus(response);
  return response.data;
});

export const createProjectJob = createAsyncThunk('projectServices/createProjectJob', async jobData => {
  const response = await apiClient.post('/jobs/', jobData);
  checkHttpStatus(response);
  return response.data;
});

// Async thunks for services performed
export const fetchServicesPerformed = createAsyncThunk(
  'projectServices/fetchServicesPerformed',
  async (params = {}) => {
    const response = await apiClient.get('/performedservices/', { params });
    checkHttpStatus(response);
    return response.data;
  },
);

export const createServicePerformed = createAsyncThunk('projectServices/createServicePerformed', async serviceData => {
  const response = await apiClient.post('/performedservices/', serviceData);
  checkHttpStatus(response);
  return response.data;
});

// Async thunk for dashboard
export const fetchDashboardData = createAsyncThunk('projectServices/fetchDashboardData', async () => {
  const response = await apiClient.get('/jobs/dashboard/');
  checkHttpStatus(response);
  return response.data;
});

// Async thunks for site groups
export const fetchSiteGroups = createAsyncThunk('projectServices/fetchSiteGroups', async (params = {}) => {
  const response = await apiClient.get('/sitegroups/', { params });
  checkHttpStatus(response);
  return response.data;
});

// Async thunks for sites
export const fetchSites = createAsyncThunk('projectServices/fetchSites', async (params = {}) => {
  const response = await apiClient.get('/sites/', { params });
  checkHttpStatus(response);
  return response.data;
});

const initialState = {
  // Normalized project jobs state
  projectJobs: projectJobsAdapter.getInitialState({
    loading: false,
    error: null,
    currentJob: null,
  }),
  // Normalized services performed state
  servicesPerformed: servicesPerformedAdapter.getInitialState({
    loading: false,
    error: null,
  }),
  // Service aggregations
  serviceAggregations: {
    byDate: {
      items: [],
      loading: false,
      error: null,
    },
    byService: {
      items: [],
      loading: false,
      error: null,
    },
    byCrewService: {
      items: [],
      loading: false,
      error: null,
    },
  },
  // Dashboard
  dashboard: {
    pendingJobs: [],
    recentJobs: [],
    userCrews: [],
    stats: {},
    loading: false,
    error: null,
  },
  // Normalized site groups state
  siteGroups: siteGroupsAdapter.getInitialState({
    loading: false,
    error: null,
    filters: {
      page: 0,
      search: '',
      sortBy: 'name',
      sortDesc: false,
      entryCount: 10,
    },
    selectedSiteGroup: null,
  }),
  // Normalized sites state
  sites: sitesAdapter.getInitialState({
    loading: false,
    error: null,
    filters: {
      page: 0,
      search: '',
      sortBy: 'name',
      sortDesc: false,
      entryCount: 10,
    },
  }),
};

const projectServicesSlice = createSlice({
  name: 'projectServices',
  initialState,
  reducers: {
    clearProjectJobError: state => {
      state.projectJobs.error = null;
    },
    clearServicesPerformedError: state => {
      state.servicesPerformed.error = null;
    },
    setCurrentJob: (state, action) => {
      state.projectJobs.currentJob = action.payload;
    },
    // Site groups actions
    setSiteGroupPage: (state, action) => {
      state.siteGroups.filters.page = action.payload;
    },
    setSiteGroupSearch: (state, action) => {
      state.siteGroups.filters.search = action.payload;
    },
    setSiteGroupSortBy: (state, action) => {
      state.siteGroups.filters.sortBy = action.payload;
    },
    setSiteGroupSortDesc: (state, action) => {
      state.siteGroups.filters.sortDesc = action.payload;
    },
    setSiteGroupEntryCount: (state, action) => {
      state.siteGroups.filters.entryCount = action.payload;
    },
    setSelectedSiteGroup: (state, action) => {
      state.siteGroups.selectedSiteGroup = action.payload;
    },
    // Sites actions
    resetSites: state => {
      state.sites = sitesAdapter.getInitialState({
        loading: false,
        error: null,
        filters: {
          page: 0,
          search: '',
          sortBy: 'name',
          sortDesc: false,
          entryCount: 10,
        },
      });
    },
    setSitePage: (state, action) => {
      state.sites.filters.page = action.payload;
    },
    setSiteSearch: (state, action) => {
      state.sites.filters.search = action.payload;
    },
    setSiteSortBy: (state, action) => {
      state.sites.filters.sortBy = action.payload;
    },
    setSiteSortDesc: (state, action) => {
      state.sites.filters.sortDesc = action.payload;
    },
    setSiteEntryCount: (state, action) => {
      state.sites.filters.entryCount = action.payload;
    },
    setSiteFilters: (state, action) => {
      state.sites.filters = { ...state.sites.filters, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Project jobs reducers
      .addCase(fetchProjectJobs.pending, state => {
        state.projectJobs.loading = true;
        state.projectJobs.error = null;
      })
      .addCase(fetchProjectJobs.fulfilled, (state, action) => {
        state.projectJobs.loading = false;
        const jobs = action.payload.results || action.payload;
        projectJobsAdapter.setAll(state.projectJobs, jobs);
      })
      .addCase(fetchProjectJobs.rejected, (state, action) => {
        state.projectJobs.loading = false;
        state.projectJobs.error = action.error.message;
      })
      .addCase(fetchProjectJob.fulfilled, (state, action) => {
        state.projectJobs.currentJob = action.payload;
        projectJobsAdapter.upsertOne(state.projectJobs, action.payload);
      })
      .addCase(createProjectJob.fulfilled, (state, action) => {
        projectJobsAdapter.addOne(state.projectJobs, action.payload);
      })
      .addCase(updateProjectJob.fulfilled, (state, action) => {
        projectJobsAdapter.upsertOne(state.projectJobs, action.payload);
        if (state.projectJobs.currentJob?.id === action.payload.id) {
          state.projectJobs.currentJob = action.payload;
        }
      })
      .addCase(approveProjectJob.fulfilled, (state, action) => {
        projectJobsAdapter.upsertOne(state.projectJobs, action.payload);
        if (state.projectJobs.currentJob?.id === action.payload.id) {
          state.projectJobs.currentJob = action.payload;
        }
      })
      .addCase(deleteProjectJob.fulfilled, (state, action) => {
        projectJobsAdapter.removeOne(state.projectJobs, action.payload);
        if (state.projectJobs.currentJob?.id === action.payload) {
          state.projectJobs.currentJob = null;
        }
      })
      // Services performed reducers
      .addCase(fetchServicesPerformed.pending, state => {
        state.servicesPerformed.loading = true;
        state.servicesPerformed.error = null;
      })
      .addCase(fetchServicesPerformed.fulfilled, (state, action) => {
        state.servicesPerformed.loading = false;
        const services = action.payload.results || action.payload;
        servicesPerformedAdapter.setAll(state.servicesPerformed, services);
      })
      .addCase(fetchServicesPerformed.rejected, (state, action) => {
        state.servicesPerformed.loading = false;
        state.servicesPerformed.error = action.error.message;
      })
      .addCase(createServicePerformed.fulfilled, (state, action) => {
        servicesPerformedAdapter.addOne(state.servicesPerformed, action.payload);
      })
      .addCase(updateServicePerformed.fulfilled, (state, action) => {
        servicesPerformedAdapter.upsertOne(state.servicesPerformed, action.payload);
      })
      .addCase(deleteServicePerformed.fulfilled, (state, action) => {
        servicesPerformedAdapter.removeOne(state.servicesPerformed, action.payload);
      })
      // Service aggregations reducers
      .addCase(fetchServiceAggregationsByDate.pending, state => {
        state.serviceAggregations.byDate.loading = true;
        state.serviceAggregations.byDate.error = null;
      })
      .addCase(fetchServiceAggregationsByDate.fulfilled, (state, action) => {
        state.serviceAggregations.byDate.loading = false;
        state.serviceAggregations.byDate.items = action.payload.results || action.payload;
      })
      .addCase(fetchServiceAggregationsByDate.rejected, (state, action) => {
        state.serviceAggregations.byDate.loading = false;
        state.serviceAggregations.byDate.error = action.error.message;
      })
      .addCase(fetchServiceAggregationsByService.pending, state => {
        state.serviceAggregations.byService.loading = true;
        state.serviceAggregations.byService.error = null;
      })
      .addCase(fetchServiceAggregationsByService.fulfilled, (state, action) => {
        state.serviceAggregations.byService.loading = false;
        state.serviceAggregations.byService.items = action.payload.results || action.payload;
      })
      .addCase(fetchServiceAggregationsByService.rejected, (state, action) => {
        state.serviceAggregations.byService.loading = false;
        state.serviceAggregations.byService.error = action.error.message;
      })
      .addCase(fetchServiceAggregationsByCrewService.pending, state => {
        state.serviceAggregations.byCrewService.loading = true;
        state.serviceAggregations.byCrewService.error = null;
      })
      .addCase(fetchServiceAggregationsByCrewService.fulfilled, (state, action) => {
        state.serviceAggregations.byCrewService.loading = false;
        state.serviceAggregations.byCrewService.items = action.payload.results || action.payload;
      })
      .addCase(fetchServiceAggregationsByCrewService.rejected, (state, action) => {
        state.serviceAggregations.byCrewService.loading = false;
        state.serviceAggregations.byCrewService.error = action.error.message;
      })
      // Dashboard reducers
      .addCase(fetchDashboardData.pending, state => {
        state.dashboard.loading = true;
        state.dashboard.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.pendingJobs = action.payload.pending_jobs || [];
        state.dashboard.recentJobs = action.payload.recent_jobs || [];
        state.dashboard.userCrews = action.payload.user_crews || [];
        state.dashboard.stats = action.payload.stats || {};
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.error = action.error.message;
      })
      // Site groups reducers
      .addCase(fetchSiteGroups.pending, state => {
        state.siteGroups.loading = true;
        state.siteGroups.error = null;
      })
      .addCase(fetchSiteGroups.fulfilled, (state, action) => {
        state.siteGroups.loading = false;
        const siteGroups = action.payload.results || action.payload;
        siteGroupsAdapter.setAll(state.siteGroups, siteGroups);
      })
      .addCase(fetchSiteGroups.rejected, (state, action) => {
        state.siteGroups.loading = false;
        state.siteGroups.error = action.error.message;
      })
      // Sites reducers
      .addCase(fetchSites.pending, state => {
        state.sites.loading = true;
        state.sites.error = null;
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        state.sites.loading = false;
        const sites = action.payload.results || action.payload;
        sitesAdapter.setAll(state.sites, sites);
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.sites.loading = false;
        state.sites.error = action.error.message;
      });
  },
});

export const {
  clearProjectJobError,
  clearServicesPerformedError,
  setCurrentJob,
  setSiteGroupPage,
  setSiteGroupSearch,
  setSiteGroupSortBy,
  setSiteGroupSortDesc,
  setSiteGroupEntryCount,
  setSelectedSiteGroup,
  resetSites,
  setSitePage,
  setSiteSearch,
  setSiteSortBy,
  setSiteSortDesc,
  setSiteEntryCount,
  setSiteFilters,
} = projectServicesSlice.actions;

// Entity adapter selectors
const projectJobsSelectors = projectJobsAdapter.getSelectors(state => state.services.projectJobs);
const servicesPerformedSelectors = servicesPerformedAdapter.getSelectors(state => state.services.servicesPerformed);
const siteGroupsSelectors = siteGroupsAdapter.getSelectors(state => state.services.siteGroups);
const sitesSelectors = sitesAdapter.getSelectors(state => state.services.sites);

// Basic selectors
export const selectProjectJobs = projectJobsSelectors.selectAll;
export const selectProjectJobsLoading = state => state.services.projectJobs.loading;
export const selectProjectJobsError = state => state.services.projectJobs.error;
export const selectCurrentJob = state => state.services.projectJobs.currentJob;
export const selectProjectJobById = projectJobsSelectors.selectById;

export const selectServicesPerformed = servicesPerformedSelectors.selectAll;
export const selectServicesPerformedLoading = state => state.services.servicesPerformed.loading;
export const selectServicesPerformedError = state => state.services.servicesPerformed.error;
export const selectServicePerformedById = servicesPerformedSelectors.selectById;

export const selectServiceAggregationsByDate = state => state.services.serviceAggregations.byDate.items;
export const selectServiceAggregationsByDateLoading = state => state.services.serviceAggregations.byDate.loading;
export const selectServiceAggregationsByService = state => state.services.serviceAggregations.byService.items;
export const selectServiceAggregationsByServiceLoading = state => state.services.serviceAggregations.byService.loading;
export const selectServiceAggregationsByCrewService = state => state.services.serviceAggregations.byCrewService.items;
export const selectServiceAggregationsByCrewServiceLoading = state =>
  state.services.serviceAggregations.byCrewService.loading;

// Site groups selectors
export const selectSiteGroups = siteGroupsSelectors.selectAll;
export const selectSiteGroupsLoading = state => state.services.siteGroups.loading;
export const selectSiteGroupsError = state => state.services.siteGroups.error;
export const selectSiteGroupById = siteGroupsSelectors.selectById;
export const selectSelectedSiteGroup = state => state.services.siteGroups.selectedSiteGroup;
export const selectSiteGroupFilters = state => state.services.siteGroups.filters;

// Site groups filter selectors (for backwards compatibility)
export const pageSelector = state => state.services.siteGroups.filters.page;
export const searchSelector = state => state.services.siteGroups.filters.search;
export const sortBySelector = state => state.services.siteGroups.filters.sortBy;
export const sortDescSelector = state => state.services.siteGroups.filters.sortDesc;
export const entryCountSelector = state => state.services.siteGroups.filters.entryCount;
export const selectedSiteGroupSelector = state => state.services.siteGroups.selectedSiteGroup;

// Sites selectors
export const selectSites = sitesSelectors.selectAll;
export const selectSitesLoading = state => state.services.sites.loading;
export const selectSitesError = state => state.services.sites.error;
export const selectSiteById = sitesSelectors.selectById;
export const selectSiteFilters = state => state.services.sites.filters;

// Sites filter selectors (for backwards compatibility)
export const sitePageSelector = state => state.services.sites.filters.page;
export const siteSearchSelector = state => state.services.sites.filters.search;
export const siteSortBySelector = state => state.services.sites.filters.sortBy;
export const siteSortDescSelector = state => state.services.sites.filters.sortDesc;
export const siteEntryCountSelector = state => state.services.sites.filters.entryCount;
export const siteFiltersSelector = state => state.services.sites.filters;

// Memoized complex selectors
export const selectPendingJobs = state => {
  const jobs = selectProjectJobs(state);
  return jobs.filter(job => job.pending);
};

export const selectApprovedJobs = state => {
  const jobs = selectProjectJobs(state);
  return jobs.filter(job => !job.pending);
};

export const selectJobsByCrewId = crewId => state => {
  const jobs = selectProjectJobs(state);
  return jobs.filter(job => job.crew?.id === crewId);
};

export const selectServicesByJobId = jobId => state => {
  const services = selectServicesPerformed(state);
  return services.filter(service => service.job?.id === jobId);
};

// Dashboard selectors
export const selectDashboardPendingJobs = state => state.services.dashboard.pendingJobs;
export const selectDashboardRecentJobs = state => state.services.dashboard.recentJobs;
export const selectDashboardUserCrews = state => state.services.dashboard.userCrews;
export const selectDashboardStats = state => state.services.dashboard.stats;
export const selectDashboardLoading = state => state.services.dashboard.loading;

export default projectServicesSlice.reducer;
